import React, { useEffect, useRef, useState } from 'react';
import { Fab, IconButton, CircularProgress } from '@material-ui/core';
import { Chat as ChatIcon, Close } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { RootStateOrAny } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useStyles } from './styles';
import { shouldEnableRamAssistant } from '../../utils/ramAssistantFeatureFlags';
import { BRANDS } from '../../types/brands';
import { generateRamAssistantUrl } from './urlGenerator';

export interface RamAssistantProps {
  assistantUrl?: string;
  buttonColor?: 'primary' | 'secondary' | 'default';
  position?: {
    bottom?: number;
    right?: number;
  };
  iframeWidth?: number;
  iframeHeight?: number;
}

const RamAssistant: React.FC<RamAssistantProps> = ({
  assistantUrl,
  buttonColor = 'primary',
  position = { bottom: 24, right: 24 },
  iframeWidth = 400,
  iframeHeight = 600,
}) => {
  const classes = useStyles();
  const { ramAssistantFlags } = useFlags();
  const state = useSelector((state: RootStateOrAny) => state);

  const selectedBrand: BRANDS = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasOpened, setHasOpened] = useState<boolean>(false);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: iframeWidth, height: iframeHeight });

  const startRef = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const rafRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Check if RAM Assistant should be enabled based on feature flags
  const isRamAssistantEnabled = shouldEnableRamAssistant(
    state,
    ramAssistantFlags,
  );

  const handleOpen = () => {
    setIsOpen(true);
    if (!hasOpened) {
      setHasOpened(true);
      setIsLoading(true);
    }
  };

  const handleClose = () => {
    // Hide only (keep iframe mounted) and reset size to defaults
    setIsOpen(false);
    setIsResizing(false);
    setIsLoading(false);
    startRef.current = null;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setDimensions({ width: iframeWidth, height: iframeHeight });
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  // Prevent click events from bubbling up to the container
  const handleChildClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  // Global click handler to close assistant when clicking anywhere outside
  useEffect(() => {
    if (!isOpen) return;

    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as Element;

      if (containerRef.current && !containerRef.current.contains(target)) {
        handleClose();
        return;
      }

      // Fallback: use data attribute if ref is not available
      const container = document.querySelector(
        '[data-testid="ram-assistant-container"]',
      );
      if (container && !container.contains(target)) {
        handleClose();
      }
    };

    document.addEventListener('click', handleGlobalClick);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [isOpen]);

  // Begin resize from top-left corner
  const handleTopLeftMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: dimensions.width,
      height: dimensions.height,
    };
    setIsResizing(true);
  };

  // Track mouse move globally during resize
  useEffect(() => {
    if (!isResizing) return;

    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current) return;

      const deltaX = e.clientX - startRef.current.x;
      const deltaY = e.clientY - startRef.current.y;

      const maxWidth = Math.max(
        300,
        window.innerWidth - (position.right || 24) - 24,
      );
      const maxHeight = Math.max(
        200,
        window.innerHeight - (position.bottom || 24) - 24,
      );

      const nextWidth = Math.min(
        Math.max(300, startRef.current.width - deltaX),
        maxWidth,
      );
      const nextHeight = Math.min(
        Math.max(200, startRef.current.height - deltaY),
        maxHeight,
      );

      // Avoid extra work if nothing changed
      if (nextWidth === dimensions.width && nextHeight === dimensions.height) {
        return;
      }

      // Throttle via rAF
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          setDimensions({ width: nextWidth, height: nextHeight });
          rafRef.current = null;
        });
      }
    };

    const onMouseUp = () => {
      setIsResizing(false);
      startRef.current = null;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp, { once: true });

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    // Do not include dimensions in deps to avoid listener churn on each frame
  }, [isResizing, position.bottom, position.right]);

  const buttonStyle = {
    bottom: `${position.bottom}px`,
    right: `${position.right}px`,
  } as React.CSSProperties;

  // Get the appropriate button class based on selected brand
  const getButtonClass = () => {
    switch (selectedBrand) {
      case 'BK':
        return classes.ramAssistantButtonBK;
      case 'PLK':
        return classes.ramAssistantButtonPLK;
      case 'TH':
        return classes.ramAssistantButtonTH;
      default:
        return classes.ramAssistantButton;
    }
  };

  const containerStyle = {
    bottom: `${position.bottom || 24}px`,
    right: `${position.right || 24}px`,
    width: `${dimensions.width}px`,
    height: `${dimensions.height}px`,
    display: isOpen ? 'block' : 'none',
  } as React.CSSProperties;

  // Don't render if feature flags disable RAM Assistant
  if (!isRamAssistantEnabled) {
    return null;
  }

  return (
    <>
      {/* RAM Assistant Button */}
      <Fab
        color={buttonColor}
        className={getButtonClass()}
        style={buttonStyle}
        onClick={handleOpen}
        aria-label="Open RAM Assistant"
      >
        <ChatIcon />
      </Fab>

      {/* RAM Assistant Floating Container - keep mounted after first open */}
      {hasOpened && (
        <div
          ref={containerRef}
          className={classes.ramAssistantFloatingContainer}
          style={containerStyle}
          data-testid="ram-assistant-container"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* Hover Header with Close Button */}
          {isHovering && (
            <div
              className={classes.ramAssistantHeader}
              onClick={handleChildClick}
            >
              <IconButton
                size="small"
                onClick={handleClose}
                className={classes.ramAssistantCloseButton}
                title="Close"
              >
                <Close fontSize="small" />
              </IconButton>
            </div>
          )}

          {/* Top-left resize handle */}
          <div
            className={classes.topLeftResizeHandle}
            onMouseDown={handleTopLeftMouseDown}
            role="presentation"
          />

          {/* Iframe Content */}
          <div
            style={{ width: '100%', height: '100%', position: 'relative' }}
            onClick={handleChildClick}
          >
            {/* Loading Overlay */}
            {isLoading && (
              <div className={classes.loadingOverlay}>
                <CircularProgress
                  className={classes.loadingSpinner}
                  size={40}
                />
              </div>
            )}

            <iframe
              src={generateRamAssistantUrl(selectedBrand, 'RAM')}
              className={
                isResizing
                  ? `${classes.ramAssistantIframe} ${classes.iframeNoPointer}`
                  : classes.ramAssistantIframe
              }
              onLoad={handleIframeLoad}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default RamAssistant;
